import { currentLocation } from "routes/currentLocation";
import { getReduxState } from "state/middleware/errorMonitoring";

interface SpanCollectorLike {
  setAction: (action: string) => SpanCollectorLike;
  setTags: (tags: Record<string, unknown>) => SpanCollectorLike;
}

interface ErrorMonitoringClient {
  sendError: (
    error: unknown,
    callback?: (span: SpanCollectorLike) => void
  ) => void;
}

interface ReduxContext {
  tags: Record<string, unknown>;
  params: Record<string, unknown>;
}

const OMITTED_QUERY_PARAMS = new Set(["code", "token", "invitationToken"]);

const errorMonitoringSearch = (search: string): string => {
  if (!search) return "";

  const searchParams = new URLSearchParams(search);
  OMITTED_QUERY_PARAMS.forEach(param => searchParams.delete(param));

  const safeSearch = searchParams.toString();
  return safeSearch ? `?${safeSearch}` : "";
};

declare global {
  interface Window {
    testSentryError?: (message?: string) => string;
    viewReduxState?: () => unknown;
  }
}

const getReduxContextForError = (): ReduxContext => {
  try {
    const state = getReduxState();

    if (!state) {
      return { tags: {}, params: {} };
    }

    const currentUser = state.currentUser;
    const location = currentLocation();
    const tags: Record<string, unknown> = {
      redux_user_id: String(currentUser?.id || "not_logged_in"),
      redux_user_name: currentUser?.name || "not_logged_in",
      redux_user_email: currentUser?.email || "not_logged_in"
    };

    if (location) {
      tags.redux_current_path = location.pathname;
      tags.redux_current_search = errorMonitoringSearch(location.search || "");
    }

    if (currentUser) {
      tags.redux_user_token_origin = currentUser.tokenOrigin || "";
    }

    return { tags, params: {} };
  } catch (error) {
    console.error("Failed to get Redux context for error", error);
    return { tags: {}, params: {} };
  }
};

const THIRD_PARTY_ERROR_DOMAINS = [
  /rec\.sessionrewind\.com/,
  /widget\.user\.com/,
  /easil\.user\.com/,
  /\/scs\/abc-static\/_\/js\/k=gapi\./,
  /apis\.google\.com\/js\/api\.js/,
  /accounts\.google\.com\/gsi\/client/
];

export const isThirdPartyError = (error: unknown): boolean => {
  const stack = error instanceof Error ? error.stack : undefined;
  if (!stack) return false;

  const frameLines = stack
    .split("\n")
    .filter(line => /^\s+at\s/.test(line) || /https?:\/\//.test(line));

  if (frameLines.length === 0) return false;

  const hasThirdPartyFrame = frameLines.some(line =>
    THIRD_PARTY_ERROR_DOMAINS.some(pattern => pattern.test(line))
  );

  if (!hasThirdPartyFrame) return false;

  const hasAppFrame = frameLines.some(
    line =>
      /\/static\/js\//.test(line) ||
      /\/assets\//.test(line) ||
      /\/src\//.test(line)
  );

  return !hasAppFrame;
};

const isThirdPartyScriptUrl = (filename: unknown): boolean =>
  typeof filename === "string" &&
  THIRD_PARTY_ERROR_DOMAINS.some(pattern => pattern.test(filename));

const stackHasFrames = (stack: string | undefined): boolean =>
  Boolean(
    stack
      ?.split("\n")
      .some(line => /^\s+at\s/.test(line) || /https?:\/\//.test(line))
  );

const STACK_OVERFLOW_MESSAGE = /Maximum call stack size exceeded/i;

const REJECTION_REASON_DETAIL_KEYS = [
  "message",
  "error",
  "reason",
  "status",
  "statusText",
  "code",
  "type"
];

const isPrimitiveReasonDetail = (
  value: unknown
): value is string | number | boolean =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const formatUnhandledRejectionReason = (reason: unknown): string => {
  if (reason instanceof Error) return reason.message;
  if (reason === null) return "null";
  if (reason === undefined) return "undefined";

  if (typeof reason !== "object") return String(reason);

  const details = REJECTION_REASON_DETAIL_KEYS.flatMap(key => {
    const value = (reason as Record<string, unknown>)[key];
    return isPrimitiveReasonDetail(value) ? [`${key}: ${String(value)}`] : [];
  });

  if (details.length > 0) return details.join(", ");

  const keys = Object.keys(reason).slice(0, 10);
  return keys.length > 0
    ? `object with keys: ${keys.join(", ")}`
    : "empty object";
};

const createUnhandledRejectionError = (reason: unknown): Error => {
  if (reason instanceof Error) return reason;

  return new Error(
    `Unhandled promise rejection: ${formatUnhandledRejectionReason(reason)}`
  );
};

// Google Tag Manager's inline snippet intermittently throws an untraceable
// "Maximum call stack size exceeded" RangeError that originates from the
// document itself (an inline <script>) rather than one of our JS bundles.
// These arrive with no stack trace and a page URL as the filename, are not
// actionable, and flood Sentry, so we drop them at the window error handler.
export const isKnownInlineStackOverflow = (event: {
  error?: unknown;
  filename?: unknown;
}): boolean => {
  const { error, filename } = event;

  if (!(error instanceof RangeError)) return false;
  if (!STACK_OVERFLOW_MESSAGE.test(error.message)) return false;

  // A genuine app stack overflow carries a stack trace pointing at our bundles.
  // Some mobile/WebView browsers still populate `error.stack` with only the
  // error message and no frames; Sentry stores those as stackless too, so treat
  // them like the no-stack inline-script shape.
  if (stackHasFrames(error.stack)) return false;

  // Inline document scripts report the page URL as the filename, not a .js file.
  if (typeof filename === "string" && /\.js(\?|$)/.test(filename)) return false;

  return true;
};

export const initializeGlobalErrorHandlers = (
  client: ErrorMonitoringClient | null | undefined
): void => {
  if (!client || process.env.NODE_ENV === "test") {
    return;
  }

  window.addEventListener("error", event => {
    if (event.target !== window) {
      return;
    }

    if (isKnownInlineStackOverflow(event)) {
      return;
    }

    if (event.error) {
      if (isThirdPartyError(event.error)) {
        return;
      }

      const { tags } = getReduxContextForError();

      client.sendError(event.error, span => {
        span.setAction("window.onerror");
        span.setTags({
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          ...tags
        });
      });
    } else if (isThirdPartyScriptUrl(event.filename)) {
      return;
    }
  });

  window.addEventListener("unhandledrejection", event => {
    if (event.reason instanceof Error && isThirdPartyError(event.reason)) {
      return;
    }

    const error = createUnhandledRejectionError(event.reason);

    const { tags } = getReduxContextForError();

    client.sendError(error, span => {
      span.setAction("unhandledrejection");
      span.setTags({
        type: "promise_rejection",
        reason: formatUnhandledRejectionReason(event.reason),
        ...tags
      });
    });
  });

  console.info("Error monitoring: Global error handlers initialized");
};

export const createTestErrorHelper = (
  client: ErrorMonitoringClient | null | undefined
): void => {
  if (!client) return;

  window.testSentryError = (message = "Test error for Sentry") => {
    try {
      const error = new Error(message);
      const { tags } = getReduxContextForError();

      console.group("📤 Sending test error to Sentry");
      console.log("Message:", message);
      console.log("Tags (metadata):", tags);
      console.log(
        "Redux breadcrumbs are included with reported errors when available."
      );
      console.groupEnd();

      client.sendError(error, span => {
        span.setAction("manual_test_error");
        span.setTags({
          test_error: true,
          triggered_from: "browser_console",
          ...tags
        });
      });

      console.log(
        "✅ Test error sent to Sentry. Check the dashboard in a few minutes."
      );
      return "Error sent successfully";
    } catch (error) {
      console.error("❌ Failed to send test error:", error);
      return "Error sending failed";
    }
  };

  window.viewReduxState = () => {
    try {
      const state = getReduxState();

      if (!state) {
        console.warn(
          "Redux state not available yet. Store may not be initialized."
        );
        return null;
      }

      const currentUser = state.currentUser;

      console.group("📊 Current Redux State");
      console.log("User ID:", currentUser?.id || "not logged in");
      console.log("Current Path:", currentLocation().pathname || "");
      console.log("Current Team:", state.ui?.currentTeam?.id || "");
      console.groupEnd();

      console.group("🔍 Full Redux State");
      console.log(state);
      console.groupEnd();

      return state;
    } catch (error) {
      console.error("Failed to view Redux state:", error);
      return null;
    }
  };

  console.info(
    "Sentry: Test helpers available\n" +
      "  - window.testSentryError('message') - Send test error with breadcrumbs\n" +
      "  - window.viewReduxState() - View current Redux state"
  );
};
